/**
 * Call Vault routes that implement Box authentication and transfer semantics.
 *
 * Direct Box browsing uses `source-api.ts`; these functions cross DocReview's
 * authorization boundary and therefore always include organization and Vault
 * identity rather than accepting a raw Box token.
 */
import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { connectorPath, previewConnectorFeed } from '../../api'
import type {
  ConnectorFeedMutation,
  ConnectorInitialSync,
  ConnectorSchedule,
} from '../../types'
import type { BoxOAuthConfig, BoxSessionToken } from '../types'

export interface CreateBoxConnectorFeedRequest {
  box: {
    folders: Array<{ id: string; name: string }>
  }
  initialSync: ConnectorInitialSync
  schedule: ConnectorSchedule
  sourceDisplayFieldKeys: string[]
}

export const createBoxConnectorFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: CreateBoxConnectorFeedRequest,
) => (await api.post<ConnectorFeedMutation>(
  `${connectorPath(orgId, vaultId)}/feeds`,
  {
    provider: 'box',
    direction: 'inbound',
    remoteFolderId: 'box',
    remoteFolderName: 'Box',
    ...request,
  },
)).data

export const previewBoxConnectorFeed = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  request: CreateBoxConnectorFeedRequest,
) => previewConnectorFeed(api, orgId, vaultId, {
  provider: 'box',
  direction: 'inbound',
  remoteFolderId: 'box',
  remoteFolderName: 'Box',
  ...request,
})

export const getBoxOAuthConfig = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.get<BoxOAuthConfig>(
  `${connectorPath(orgId, vaultId)}/box/config`,
)).data

export const saveBoxApplicationCredentials = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  clientId: string,
  clientSecret: string,
) => api.put(`${connectorPath(orgId, vaultId)}/box/application-credentials`, {
  clientId,
  clientSecret,
})

export const deleteBoxApplicationCredentials = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => api.delete(`${connectorPath(orgId, vaultId)}/box/application-credentials`)

export const exchangeBoxCode = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  code: string,
  redirectUri: string,
) => (await api.post<BoxSessionToken>(
  `${connectorPath(orgId, vaultId)}/box/oauth/token`,
  { code, redirectUri },
)).data

export const refreshBoxSession = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => (await api.post<BoxSessionToken>(
  `${connectorPath(orgId, vaultId)}/box/session`,
)).data

export const disconnectBox = async (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
) => api.delete(`${connectorPath(orgId, vaultId)}/box/connection`)
